import type { Brand, Category, StockReport, StockReportGroup, StockValueByCurrency, Warehouse } from '@erp/shared';
import { AlertTriangle, Boxes, CircleOff, Download, Package, PackageX, Printer, TrendingDown, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Card, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/shadcn/index.js';
import { Button, Combobox, type ComboboxOption, EmptyState } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { formatDateTime, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

import { KpiCard } from './_shared/KpiCard.js';

/** How many rows a breakdown shows before "Show all". */
const COLLAPSED = 12;

/**
 * Reports → Stock: what the shop holds, what it is worth, and where it sits.
 *
 * Everything comes from GET /stock/report, which totals EVERY stock line — the
 * stock page shows one page of thousands. Value is quantity × average cost,
 * kept per currency; the headline figure is the one place they are added, and
 * only after converting each at its newest rate, which the card names.
 */
export function StockReportPage(): JSX.Element {
  const { t, i18n } = useTranslation('reports');
  const { t: tc } = useTranslation();
  const lng = i18n.language;

  const [warehouseId, setWarehouseId] = useState('all');
  const [categoryId, setCategoryId] = useState('all');
  const [brandId, setBrandId] = useState('all');
  const [report, setReport] = useState<StockReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);

  useEffect(() => {
    void (async () => {
      const [w, c, b] = await Promise.all([
        api().get<{ items: Warehouse[] }>('/warehouses').catch(() => ({ data: { items: [] } })),
        api().get<{ items: Category[] }>('/categories').catch(() => ({ data: { items: [] } })),
        api().get<{ items: Brand[] }>('/brands').catch(() => ({ data: { items: [] } })),
      ]);
      setWarehouses(w.data.items);
      setCategories(c.data.items);
      setBrands(b.data.items);
    })();
  }, []);

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (warehouseId !== 'all') p.warehouseId = warehouseId;
    if (categoryId !== 'all') p.categoryId = categoryId;
    if (brandId !== 'all') p.brandId = brandId;
    return p;
  }, [warehouseId, categoryId, brandId]);

  useEffect(() => {
    let on = true;
    setLoading(true);
    setFailed(false);
    void api().get<StockReport>('/stock/report', { params })
      .then((r) => { if (on) setReport(r.data); })
      .catch(() => { if (on) setFailed(true); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [params]);

  const nf = (n: number | string): string => formatNumber(Number(n), { locale: lng });
  const money = (v: string | number, ccy: string): string => formatMoney(String(v), { locale: lng, currency: ccy });
  const perCurrency = (values: StockValueByCurrency[]): string =>
    values.map((v) => money(v.value, v.currencyCode)).join(' · ');

  /** The stock page, filtered to exactly these lines — and to this report's filters. */
  const stockLink = (extra: Record<string, string>): string =>
    `/inventory/stock?${new URLSearchParams({ ...params, ...extra }).toString()}`;

  const header = (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold">{t('stockReport.title', { defaultValue: 'Stock report' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('stockReport.subtitle', { defaultValue: 'What you have, what it is worth, and where it sits.' })}
          {report && (
            <span className="ms-1 text-fg-subtle">
              {t('stockReport.asOf', { defaultValue: 'As of {{when}}.', when: formatDateTime(report.generatedAt, { locale: lng }) })}
            </span>
          )}
        </p>
      </div>
      <div className="flex flex-wrap gap-2 print:hidden">
        <Button variant="secondary" onClick={() => report && downloadCsv(report, t, lng)} disabled={!report}>
          <Download className="me-1.5 h-4 w-4" aria-hidden />
          {t('stockReport.exportCsv', { defaultValue: 'Export CSV' })}
        </Button>
        <Button variant="secondary" onClick={() => window.print()} disabled={!report}>
          <Printer className="me-1.5 h-4 w-4" aria-hidden />
          {t('stockReport.print', { defaultValue: 'Print' })}
        </Button>
      </div>
    </header>
  );

  const comboOptions = (rows: Array<{ id: string; label: string }>, allLabel: string): ComboboxOption[] => [
    { value: 'all', label: allLabel },
    ...rows.map((r) => ({ value: r.id, label: r.label })),
  ];
  const anyFilter = warehouseId !== 'all' || categoryId !== 'all' || brandId !== 'all';
  const filters = (
    <div className="flex flex-wrap items-center gap-2 print:hidden">
      {warehouses.length > 1 && (
        <select
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
          aria-label={t('stockReport.warehouse', { defaultValue: 'Warehouse' })}
          className="h-9 rounded-md border border-border bg-bg px-3 text-sm"
        >
          <option value="all">{t('stockReport.allWarehouses', { defaultValue: 'All warehouses' })}</option>
          {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      )}
      {categories.length > 0 && (
        <div className="w-56">
          <Combobox
            options={comboOptions(categories.map((c) => ({ id: c.id, label: c.name })), t('stockReport.allCategories', { defaultValue: 'All categories' }))}
            value={categoryId}
            onChange={(v) => setCategoryId(v ?? 'all')}
            placeholder={t('stockReport.allCategories', { defaultValue: 'All categories' })}
            ariaLabel={t('stockReport.allCategories', { defaultValue: 'All categories' })}
          />
        </div>
      )}
      {brands.length > 0 && (
        <div className="w-56">
          <Combobox
            options={comboOptions(brands.map((b) => ({ id: b.id, label: b.title })), t('stockReport.allBrands', { defaultValue: 'All brands' }))}
            value={brandId}
            onChange={(v) => setBrandId(v ?? 'all')}
            placeholder={t('stockReport.allBrands', { defaultValue: 'All brands' })}
            ariaLabel={t('stockReport.allBrands', { defaultValue: 'All brands' })}
          />
        </div>
      )}
      {anyFilter && (
        <Button variant="ghost" size="sm" onClick={() => { setWarehouseId('all'); setCategoryId('all'); setBrandId('all'); }}>
          {t('stockReport.clear', { defaultValue: 'Clear filters' })}
        </Button>
      )}
    </div>
  );

  if (!report) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {filters}
        {failed ? (
          <Card className="p-6 text-sm text-danger">{tc('errors:server', { defaultValue: 'Something went wrong. Try again.' })}</Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" />
          </div>
        )}
      </div>
    );
  }

  const s = report.summary;
  const base = report.baseCurrencyCode;
  const otherRates = report.converted.rates.filter((r) => r.currencyCode !== base);
  // Said the way the shop says it — "1 USD = 1,540 IQD" — whichever of the two
  // is the base: `rate` multiplies one unit of the currency INTO the base.
  const rateHint = otherRates
    .map((r) => {
      const m = Number(r.rate);
      const big = m >= 1;
      return t('stockReport.rate', {
        defaultValue: '1 {{from}} = {{rate}} {{to}}',
        from: big ? r.currencyCode : base,
        to: big ? base : r.currencyCode,
        rate: formatNumber(big ? m : 1 / m, { locale: lng }, { maximumFractionDigits: (big ? m : 1 / m) >= 100 ? 0 : 4 }),
      });
    })
    .join(' · ');
  const totalConverted = Number(report.converted.value);

  if (s.lines === 0) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {filters}
        <EmptyState
          icon={<Package className="h-8 w-8" aria-hidden />}
          title={t('stockReport.empty', { defaultValue: 'No stock matches' })}
          description={t('stockReport.emptyHint', { defaultValue: 'Nothing is recorded in stock for this selection.' })}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {header}
      {filters}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiCard
          label={t('stockReport.value', { defaultValue: 'Stock value' })}
          value={money(report.converted.value, base)}
          hint={[
            s.values.length > 1 ? perCurrency(s.values) : null,
            rateHint ? t('stockReport.convertedAt', { defaultValue: 'converted at {{rates}}', rates: rateHint }) : null,
          ].filter(Boolean).join(' — ') || undefined}
          icon={Wallet}
          tone="brand"
          loading={loading}
        />
        <KpiCard
          label={t('stockReport.productsInStock', { defaultValue: 'Products in stock' })}
          value={nf(s.itemsInStock)}
          hint={t('stockReport.productsOf', { defaultValue: 'Products with a stock line: {{n}}', n: nf(s.items) })}
          icon={Package}
          tone="success"
          loading={loading}
        />
        <KpiCard
          label={t('stockReport.units', { defaultValue: 'Units on hand' })}
          value={nf(s.quantity)}
          hint={t('stockReport.linesHint', { defaultValue: 'Lines (product × warehouse): {{n}}', n: nf(s.lines) })}
          icon={Boxes}
          tone="info"
          loading={loading}
        />
      </div>

      {report.converted.missing.length > 0 && (
        <Card className="border-warning p-3 text-sm text-warning">
          {t('stockReport.missingRate', {
            defaultValue: 'No exchange rate for {{list}}: that stock is not in the total above. Add a rate under Accounting → Exchange rates.',
            list: report.converted.missing.join(', '),
          })}
        </Card>
      )}

      {/* Stock health — each one opens the stock page filtered to exactly those lines. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {healthTile(t('stockReport.outOfStock', { defaultValue: 'Out of stock' }), nf(s.outOfStockLines),
          t('stockReport.lines', { defaultValue: 'lines' }), PackageX, s.outOfStockLines ? 'warning' : null, stockLink({ state: 'outOfStock' }))}
        {healthTile(t('stockReport.low', { defaultValue: 'Running low (≤ {{n}})', n: nf(s.lowThreshold) }), nf(s.lowLines),
          t('stockReport.lines', { defaultValue: 'lines' }), TrendingDown, s.lowLines ? 'warning' : null, stockLink({ state: 'low' }))}
        {healthTile(t('stockReport.negative', { defaultValue: 'Negative stock' }), nf(s.negativeLines),
          t('stockReport.lines', { defaultValue: 'lines' }), AlertTriangle, s.negativeLines ? 'danger' : null, stockLink({ state: 'negative' }))}
        {healthTile(t('stockReport.noCost', { defaultValue: 'On hand, no cost' }), nf(s.noCostLines),
          t('stockReport.noCostUnits', { defaultValue: 'lines · units: {{n}}', n: nf(s.noCostQuantity) }), CircleOff,
          s.noCostLines ? 'warning' : null, stockLink({ state: 'inStock', hasCost: 'false' }))}
      </div>
      {s.noCostLines > 0 && (
        <p className="text-xs text-fg-muted">
          {t('stockReport.noCostNote', {
            defaultValue: 'On hand with no cost — lines: {{lines}}, units: {{units}}. The value above does not include them; a cost comes from a purchase bill, a stock adjustment or the Excel import.',
            lines: nf(s.noCostLines),
            units: nf(s.noCostQuantity),
          })}
        </p>
      )}

      <Breakdown
        title={t('stockReport.byWarehouse', { defaultValue: 'By warehouse' })}
        groups={report.byWarehouse}
        noneLabel="—"
        base={base}
        total={totalConverted}
        t={t}
        lng={lng}
      />
      <Breakdown
        title={t('stockReport.byCategory', { defaultValue: 'By category' })}
        groups={report.byCategory}
        noneLabel={t('stockReport.noCategory', { defaultValue: 'No category' })}
        base={base}
        total={totalConverted}
        t={t}
        lng={lng}
      />
      <Breakdown
        title={t('stockReport.byBrand', { defaultValue: 'By brand' })}
        groups={report.byBrand}
        noneLabel={t('stockReport.noBrand', { defaultValue: 'No brand' })}
        base={base}
        total={totalConverted}
        t={t}
        lng={lng}
      />

      <Card className="overflow-hidden p-0">
        <h2 className="border-b border-border px-4 py-3 text-sm font-bold uppercase tracking-wide text-fg-muted">
          {t('stockReport.topLines', { defaultValue: 'Most valuable stock' })}
        </h2>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('stockReport.product', { defaultValue: 'Product' })}</TableHead>
              <TableHead>{t('stockReport.warehouse', { defaultValue: 'Warehouse' })}</TableHead>
              <TableHead className="text-end">{t('stockReport.quantity', { defaultValue: 'Quantity' })}</TableHead>
              <TableHead className="text-end">{t('stockReport.avgCost', { defaultValue: 'Avg cost' })}</TableHead>
              <TableHead className="text-end">{t('stockReport.valueCol', { defaultValue: 'Value' })}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.topByValue.map((l) => (
              <TableRow key={`${l.itemId}-${l.warehouseId}`}>
                <TableCell>
                  <Link to={`/inventory/items/${l.itemId}`} className="hover:text-brand">
                    <span className="font-mono text-xs">{l.itemCode}</span> · {l.itemName}
                  </Link>
                </TableCell>
                <TableCell className="text-fg-muted">{l.warehouseName}</TableCell>
                <TableCell className="text-end font-mono tabular-nums">{nf(l.quantity)}</TableCell>
                <TableCell className="text-end font-mono tabular-nums">{money(l.avgCost, l.currencyCode)}</TableCell>
                <TableCell className="text-end font-mono tabular-nums">
                  {money(l.value, l.currencyCode)}
                  {l.currencyCode !== base && l.converted !== null && (
                    <div className="text-xs text-fg-subtle">≈ {money(l.converted, base)}</div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <p className="text-xs text-fg-subtle">
        {t('stockReport.method', {
          defaultValue: 'Value = quantity × average cost of each product in each warehouse, in the currency it was costed in. The total converts each currency at its newest exchange rate.',
        })}
      </p>
    </div>
  );
}

function healthTile(
  label: string,
  value: string,
  unit: string,
  Icon: typeof PackageX,
  tone: 'warning' | 'danger' | null,
  to: string,
): JSX.Element {
  return (
    <Link
      to={to}
      className="flex flex-col gap-1 rounded-xl border border-border bg-bg p-3 transition-colors hover:border-brand print:border-border"
    >
      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-fg-muted">
        <Icon className="h-4 w-4" aria-hidden />
        {label}
      </span>
      <span className={cn('font-mono text-2xl font-semibold tabular-nums', tone === 'warning' && 'text-warning', tone === 'danger' && 'text-danger')}>
        {value}
      </span>
      <span className="text-xs text-fg-subtle">{unit}</span>
    </Link>
  );
}

function Breakdown({
  title,
  groups,
  noneLabel,
  base,
  total,
  t,
  lng,
}: {
  title: string;
  groups: StockReportGroup[];
  noneLabel: string;
  base: string;
  total: number;
  t: (key: string, opts?: Record<string, unknown>) => string;
  lng: string;
}): JSX.Element | null {
  const [all, setAll] = useState(false);
  if (groups.length === 0) return null;
  const shown = all ? groups : groups.slice(0, COLLAPSED);
  const nf = (n: number | string): string => formatNumber(Number(n), { locale: lng });
  const money = (v: string, ccy: string): string => formatMoney(v, { locale: lng, currency: ccy });
  return (
    <Card className="overflow-hidden p-0">
      <h2 className="border-b border-border px-4 py-3 text-sm font-bold uppercase tracking-wide text-fg-muted">
        {title} <span className="font-normal normal-case text-fg-subtle">· {nf(groups.length)}</span>
      </h2>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('stockReport.name', { defaultValue: 'Name' })}</TableHead>
            <TableHead className="text-end">{t('stockReport.products', { defaultValue: 'Products' })}</TableHead>
            <TableHead className="text-end">{t('stockReport.units', { defaultValue: 'Units on hand' })}</TableHead>
            <TableHead className="text-end">{t('stockReport.valueCol', { defaultValue: 'Value' })}</TableHead>
            <TableHead className="w-40">{t('stockReport.share', { defaultValue: 'Share of value' })}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map((g) => {
            const share = g.converted !== null && total > 0 ? (Number(g.converted) / total) * 100 : null;
            return (
              <TableRow key={g.id ?? 'none'}>
                <TableCell className={cn(!g.name && 'text-fg-muted')}>
                  {g.code && <span className="font-mono text-xs">{g.code} · </span>}
                  {g.name ?? noneLabel}
                  {g.noCostLines > 0 && (
                    <div className="text-xs text-warning">
                      {t('stockReport.groupNoCost', { defaultValue: 'Lines without cost: {{n}}', n: nf(g.noCostLines) })}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-end font-mono tabular-nums">{nf(g.items)}</TableCell>
                <TableCell className="text-end font-mono tabular-nums">{nf(g.quantity)}</TableCell>
                <TableCell className="text-end font-mono tabular-nums">
                  {g.converted !== null ? money(g.converted, base) : '—'}
                  {(g.values.length > 1 || (g.values[0] && g.values[0].currencyCode !== base)) && (
                    <div className="text-xs text-fg-subtle">
                      {g.values.map((v) => money(v.value, v.currencyCode)).join(' · ')}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {share !== null && (
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-bg-muted">
                        <div className="h-full rounded-full bg-brand" style={{ width: `${Math.min(100, Math.max(0, share)).toFixed(1)}%` }} />
                      </div>
                      <span className="w-12 text-end font-mono text-xs tabular-nums text-fg-muted">
                        {formatNumber(share, { locale: lng }, { maximumFractionDigits: 1 })}%
                      </span>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {groups.length > COLLAPSED && (
        <div className="border-t border-border px-4 py-2 print:hidden">
          <Button variant="ghost" size="sm" onClick={() => setAll((v) => !v)}>
            {all
              ? t('stockReport.showLess', { defaultValue: 'Show fewer' })
              : t('stockReport.showAll', { defaultValue: 'Show all ({{n}})', n: nf(groups.length) })}
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * The report as one CSV — a sheet an accountant can sum. Plain numbers (no
 * grouping, no currency signs) so a spreadsheet reads them as numbers, and a
 * UTF-8 BOM so Excel opens the Arabic names as Arabic.
 */
function downloadCsv(
  r: StockReport,
  t: (key: string, opts?: Record<string, unknown>) => string,
  lng: string,
): void {
  const q = (v: unknown): string => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows: unknown[][] = [];
  const s = r.summary;
  rows.push([t('stockReport.title', { defaultValue: 'Stock report' }), formatDateTime(r.generatedAt, { locale: lng })]);
  rows.push([]);
  rows.push([t('stockReport.value', { defaultValue: 'Stock value' }), r.converted.value, r.baseCurrencyCode]);
  for (const v of s.values) rows.push(['', v.value, v.currencyCode]);
  rows.push([t('stockReport.productsInStock', { defaultValue: 'Products in stock' }), s.itemsInStock]);
  rows.push([t('stockReport.units', { defaultValue: 'Units on hand' }), s.quantity]);
  rows.push([t('stockReport.outOfStock', { defaultValue: 'Out of stock' }), s.outOfStockLines]);
  rows.push([t('stockReport.negative', { defaultValue: 'Negative stock' }), s.negativeLines]);
  rows.push([t('stockReport.noCost', { defaultValue: 'On hand, no cost' }), s.noCostLines, s.noCostQuantity]);
  const section = (title: string, groups: StockReportGroup[]): void => {
    rows.push([]);
    rows.push([title]);
    rows.push([
      t('stockReport.name', { defaultValue: 'Name' }),
      t('stockReport.products', { defaultValue: 'Products' }),
      t('stockReport.units', { defaultValue: 'Units on hand' }),
      `${t('stockReport.valueCol', { defaultValue: 'Value' })} (${r.baseCurrencyCode})`,
      t('stockReport.byCurrency', { defaultValue: 'By currency' }),
    ]);
    for (const g of groups) {
      rows.push([
        [g.code, g.name].filter(Boolean).join(' · ') || '—',
        g.items,
        g.quantity,
        g.converted ?? '',
        g.values.map((v) => `${v.value} ${v.currencyCode}`).join(' | '),
      ]);
    }
  };
  section(t('stockReport.byWarehouse', { defaultValue: 'By warehouse' }), r.byWarehouse);
  section(t('stockReport.byCategory', { defaultValue: 'By category' }), r.byCategory);
  section(t('stockReport.byBrand', { defaultValue: 'By brand' }), r.byBrand);
  rows.push([]);
  rows.push([t('stockReport.topLines', { defaultValue: 'Most valuable stock' })]);
  for (const l of r.topByValue) {
    rows.push([`${l.itemCode} · ${l.itemName}`, l.warehouseName, l.quantity, l.avgCost, l.value, l.currencyCode, l.converted ?? '']);
  }
  const csv = `\uFEFF${rows.map((row) => row.map(q).join(',')).join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `stock-report-${r.generatedAt.slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
